import { Edit, MoreHoriz, PlayArrow } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Tooltip } from '~/components/Tooltip';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { FolderIcon, FolderListThumbnail } from '~/components/folder-breadcrumbs/styles';
import { LayoutSkeleton } from '~/components/layout-skeleton';
import { useLink } from '~/hooks/link';
import type {
  ShowList__ShowFolder as Folder,
  ShowList__Show as Show,
} from '../../queries/queries.generated';
import { Menu, ShowLocation } from '../components';

type ShowOrFolder = Show | Folder;

export interface UseColumnsProps {
  selectedShow?: Show;
  onSelectedShow: (show: Show | undefined) => void;
  search?: boolean;
}

export const useColumns = ({
  selectedShow,
  onSelectedShow,
  search = false,
}: UseColumnsProps): GridColDef<ShowOrFolder>[] => {
  const { t } = useTranslation();
  const link = useLink();
  const [searchParams] = useSearchParams();
  searchParams.delete('viewId');

  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(() => {
    const columns: GridColDef<ShowOrFolder>[] = [
      {
        field: 'thumbnailUri',
        ...hiddenHeader(t('common.labels.thumbnail')),
        width: 84,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return row.__typename === 'ShowFolder' ? (
            <Box display="flex" alignItems="center" justifyContent="center" width="100%">
              <FolderIcon sx={{ color: 'primary.main' }} />
            </Box>
          ) : (
            <FolderListThumbnail>
              <LayoutSkeleton
                layout={row.defaultView.layout}
                style={{ objectFit: 'contain', width: '100%', height: '100%' }}
              />
            </FolderListThumbnail>
          );
        },
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        minWidth: isSmallAndUp ? 180 : 150,
      },
      {
        field: 'deviceGroups',
        ...hiddenHeader(t('common.labels.status')),
        sortable: false,
        renderCell: ({ row }) => {
          return row.__typename === 'Show'
            ? row.defaultForDeviceGroups && row.defaultForDeviceGroups.length > 0 && (
                <Tooltip
                  title={t('pages.shows.list.labels.default_show_tooltip', {
                    showName: row.name,
                    deviceGroups: row.defaultForDeviceGroups.map((dg) => dg.name).join(', '),
                  })}
                >
                  <Chip label={t('pages.shows.list.labels.default_show')} color="success" />
                </Tooltip>
              )
            : null;
        },
        minWidth: isSmallAndUp ? 150 : 0,
      },
      {
        field: 'showCount',
        headerName: t('common.tabs.shows'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          if (row.__typename === 'ShowFolder') {
            return <Chip color="primary" label={row.itemCount || 0} />;
          }
          return '--';
        },
        minWidth: 150,
      },
      {
        field: 'viewIds',
        headerName: t('pages.shows.list.columns.views'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          if (row.__typename === 'ShowFolder') return '--';
          return <Chip color="primary" label={row.viewIds.length || 0} />;
        },
        minWidth: 150,
      },
    ];

    if (search) {
      columns.push({
        field: 'location',
        headerName: t('pages.shows.list.columns.location'),
        minWidth: 250,
        renderCell: ({ row }) => {
          if (row.__typename === 'ShowFolder') return null;
          return <ShowLocation showFolderId={row.showFolderId} />;
        },
        sortable: false,
      });
    }

    columns.push({
      field: 'actions',
      type: 'actions',
      align: 'right',
      minWidth: isSmallAndUp ? 150 : 0,
      getActions: ({ row }) => {
        if (row.__typename === 'ShowFolder') {
          return [
            <Menu
              key={`more-${row.id}`}
              item={row}
              selectedShow={undefined}
              onSelectedShow={() => {
                undefined;
              }}
              Icon={MoreHoriz}
              buttonType="action"
            />,
          ];
        }

        const showPath = `/shows/${row.showFolderId}/show/${row.id}`;
        return [
          <ActionButtonLink
            key={`edit-${row.id}`}
            title={t('common.actions.edit')}
            Icon={Edit}
            to={{ ...link(`${showPath}/design`), search: searchParams.toString() }}
          />,
          <ActionButtonLink
            key={`preview-${row.id}`}
            title={t('common.labels.preview')}
            Icon={PlayArrow}
            to={{ ...link(`${showPath}/preview`), search: searchParams.toString() }}
          />,
          <Menu
            key={`more-action-${row.id}`}
            item={row}
            selectedShow={selectedShow}
            onSelectedShow={onSelectedShow}
            Icon={MoreHoriz}
            buttonType="action"
          />,
        ];
      },
    });

    return columns;
  }, [isSmallAndUp, link, onSelectedShow, search, searchParams, selectedShow, t]);
};
